import React, { useEffect } from 'react';
import { Text, Stage, Layer } from 'react-konva';
import { useQuery } from '@apollo/client';
import useStores from '../hooks/useStores';
import { splitByThreshold } from '../../utils/helpers';
import useFontLoaded from '../hooks/useFontLoaded';
import { Page, WonderlineActivityType, WonderlineStepType } from '../types';
import { GET_SETTING } from '../../graphql/queries/oneSongWonder/onesongwonder';
import useGetWonderLines from '../../hooks/OneSongWonder/useGetWonderLines';
import WonderInformation from '../../components/Shared/Modals/OneSongWonder/WonderInformation';
import { showModal } from '../../styles/js/modals';
import ActivitiesModal from '../../components/Shared/Modals/OneSongWonder/ActivitiesModal';
import CloudIcon from '../../Icons/CloudIcon';
import BibleIcon from '../../Icons/BibleIcon';
import JoinDotsIcon from '../../Icons/JoinDotsIcon';
import YouMeIcon from '../../Icons/YouMeIcon';
import SchoolBeyond from '../../Icons/SchoolBeyond';
import PrayerIcon from '../../Icons/PrayerIcon';
import useMediaQuery from '../../hooks/Others/useMediaQuery';
import WonderSettings from '../../components/Shared/Modals/OneSongWonder/WonderSettings';
import WonderListModal from '../../components/Shared/Modals/OneSongWonder/WonderList';
import WonderActivitiesModal from '../../components/Shared/Modals/OneSongWonder/WonderActivities';
import { isPlayerFullscreen, playerActive } from '../../utils/reactiveVars';
import QAReveal from './QAReveal';
import '../css/one-song-wonder.css';

type Props = {
  id: number;
  title: string;
  page: Page;
  toggleFullScreen: () => void;
};

export const WonderSteps = [
  {
    icon: <CloudIcon fill="#7687c2" />,
    title: 'Wonder 1',
    type: WonderlineStepType.WONDER_1,
  },
  {
    icon: <CloudIcon fill="#7687c2" />,
    title: 'Wonder 2',
    type: WonderlineStepType.WONDER_2,
  },
  {
    icon: <CloudIcon fill="#7687c2" />,
    title: 'Wonder 3',
    type: WonderlineStepType.WONDER_3,
  },
  {
    icon: <BibleIcon fill="#7687c2" />,
    title: 'The bible says',
    type: WonderlineStepType.THE_BIBLE_SAYS,
  },
  {
    icon: <JoinDotsIcon fill="#7687c2" />,
    title: 'Join The Dots',
    type: WonderlineStepType.JOIN_THE_DOTS,
  },
  {
    icon: <YouMeIcon fill="#7687c2" />,
    title: 'You & Me',
    type: WonderlineStepType.YOU_AND_ME,
  },
  {
    icon: <SchoolBeyond fill="#7687c2" />,
    title: 'School & beyond',
    type: WonderlineStepType.SCHOOL_BEYOND,
  },
  {
    icon: <PrayerIcon fill="#7687c2" />,
    title: 'Prayer response',
    type: WonderlineStepType.PRAYER_RESPONSE,
  },
];

const getStepClassName = (active: boolean, first?: boolean, last?: boolean) => {
  const classNames = ['one-song-wonder__step'];

  if (active) classNames.push('is-active');
  if (first) classNames.push('is-first');
  if (last) classNames.push('is-last');

  return classNames.join(' ');
};

const OSW_QUERY_KEYS = {
  lineIndex: 'oswLineIndex',
  line: 'oswLine',
  step: 'oswStep',
  songId: 'oswSongId',
};

const OneSongWonder: React.FC<Props> = ({ page, title, toggleFullScreen }) => {
  const isMobile = useMediaQuery(
    '(max-width: 1199px), screen and (min-width: 428px) and (max-width: 926px) and (orientation: landscape)'
  );
  const { mediaPlayerStore } = useStores();
  const [showDetails, setShowDetails] = React.useState(false);
  const [autoSelected, setAutoSelected] = React.useState(false);
  const [restoredFromUrl, setRestoredFromUrl] = React.useState(false);
  const [selectedLineIndex, setSelectedLineIndex] = React.useState(-1);
  const [selectedLine, setSelectedLine] = React.useState('');
  const [selectedLineSteps, setSelectedLineSteps] = React.useState<any[]>([]);
  const [selectedWonderLine, setSelectedWonderLine] = React.useState<any>(null);
  const [selectedActivity, setSelectedActivity] = React.useState(null);
  const [selectedStep, setSelectedStep] = React.useState(0);

  const { listWonderLines, loading, data } = useGetWonderLines(
    mediaPlayerStore.song.id
  );

  const { data: settingData, loading: settingLoading } = useQuery(GET_SETTING, {
    variables: { group: 'One-Song Wonders' },
  });

  const [activitiesOpen, setActivitiesOpen] = React.useState(false);
  const [isLoadingNewSong, setIsLoadingNewSong] = React.useState(true);
  const currentSongId = String(mediaPlayerStore.song.id);
  const previousSongIdRef = React.useRef(currentSongId);
  const isSwitchingSong = previousSongIdRef.current !== currentSongId;
  const wonderLines = React.useMemo(() => data?.listWonderLines ?? [], [data]);
  const isDataFromAnotherSong =
    wonderLines.length > 0 &&
    wonderLines.every(
      (line: { song?: { id?: string | number } }) =>
        String(line?.song?.id) !== currentSongId
    );
  const currentSongWonderLines = React.useMemo(
    () => (isDataFromAnotherSong ? [] : wonderLines),
    [isDataFromAnotherSong, wonderLines]
  );
  const shouldHideContent =
    isSwitchingSong || loading || isLoadingNewSong || isDataFromAnotherSong;

  useEffect(() => {
    previousSongIdRef.current = currentSongId;
  }, [currentSongId]);

  const updateWonderInUrl = React.useCallback(
    (lineIndex: number, step: number) => {
      const params = new URLSearchParams(window.location.search);
      params.set(OSW_QUERY_KEYS.songId, String(mediaPlayerStore.song.id));
      params.set(OSW_QUERY_KEYS.lineIndex, String(lineIndex));
      params.set(OSW_QUERY_KEYS.step, String(step));
      params.delete(OSW_QUERY_KEYS.line);
      const nextUrl = `${window.location.pathname}?${params.toString()}${
        window.location.hash
      }`;
      window.history.replaceState({}, '', nextUrl);
    },
    [mediaPlayerStore.song.id]
  );

  const clearOswParamsFromUrl = React.useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    params.delete(OSW_QUERY_KEYS.songId);
    params.delete(OSW_QUERY_KEYS.lineIndex);
    params.delete(OSW_QUERY_KEYS.step);
    params.delete(OSW_QUERY_KEYS.line);
    const nextUrl = `${window.location.pathname}${
      params.toString() ? '?' + params.toString() : ''
    }${window.location.hash}`;
    window.history.replaceState({}, '', nextUrl);
  }, []);

  const safeExitFullscreen = React.useCallback(() => {
    if (document.fullscreenElement && document.hasFocus()) {
      toggleFullScreen();
    }
    clearOswParamsFromUrl();
  }, [toggleFullScreen, clearOswParamsFromUrl]);

  useEffect(() => {
    setIsLoadingNewSong(true);
    setShowDetails(false);
    setAutoSelected(false);
    setRestoredFromUrl(false);
    setSelectedLineIndex(-1);
    setSelectedLine('');
    setSelectedLineSteps([]);
    setSelectedWonderLine(null);
    setSelectedActivity(null);
    setSelectedStep(0);

    listWonderLines({
      variables: { filter: { songId: mediaPlayerStore.song.id } },
    });
  }, [mediaPlayerStore.song.id, listWonderLines]);

  useEffect(() => {
    if (!loading && data && !isDataFromAnotherSong) {
      setIsLoadingNewSong(false);
    }
  }, [loading, data, isDataFromAnotherSong]);

  useEffect(() => {
    if (shouldHideContent || restoredFromUrl) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const lineIndexFromUrl = params.get(OSW_QUERY_KEYS.lineIndex);
    const stepFromUrl = params.get(OSW_QUERY_KEYS.step);
    const songIdFromUrl = params.get(OSW_QUERY_KEYS.songId);
    let found: any;
    let foundIndex = -1;

    if (!lineIndexFromUrl) {
      return;
    }

    if (
      songIdFromUrl &&
      String(songIdFromUrl) !== String(mediaPlayerStore.song.id)
    ) {
      return;
    }

    if (lineIndexFromUrl) {
      const parsedLineIndex = Number.parseInt(lineIndexFromUrl, 10);
      if (!Number.isNaN(parsedLineIndex)) {
        foundIndex = parsedLineIndex;
        found = currentSongWonderLines[parsedLineIndex];
      }
    }

    const parsedStep = Number.parseInt(stepFromUrl || '0', 10);
    if (!found) {
      foundIndex = 0;
      found = currentSongWonderLines[0];
    }

    if (!found) {
      return;
    }

    const initialStep = Number.isNaN(parsedStep)
      ? 0
      : Math.max(0, Math.min(parsedStep, found.wonderLineSteps.length - 1));
    setShowDetails(true);
    setSelectedWonderLine(found);
    setSelectedLineSteps(found.wonderLineSteps);
    setSelectedLineIndex(foundIndex);
    setSelectedLine(found.songLine);
    setSelectedStep(initialStep);
    setRestoredFromUrl(true);
  }, [
    shouldHideContent,
    restoredFromUrl,
    mediaPlayerStore.song.id,
    currentSongWonderLines,
  ]);

  useEffect(() => {
    if (restoredFromUrl) {
      return;
    }
    if (
      !shouldHideContent &&
      currentSongWonderLines.length === 1 &&
      !autoSelected
    ) {
      const params = new URLSearchParams(window.location.search);
      if (params.get(OSW_QUERY_KEYS.lineIndex)) {
        return;
      }
      setShowDetails(true);
      setAutoSelected(true);
      setSelectedWonderLine(currentSongWonderLines[0]);
      setSelectedLineSteps(currentSongWonderLines[0].wonderLineSteps);
      setSelectedLineIndex(0);
      setSelectedLine(currentSongWonderLines[0].songLine);
      updateWonderInUrl(0, 0);
    }
  }, [
    shouldHideContent,
    currentSongWonderLines,
    autoSelected,
    restoredFromUrl,
    updateWonderInUrl,
  ]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showDetails || autoSelected) {
          setShowDetails(false);
          setAutoSelected(false);
        }
        mediaPlayerStore.setShowOSW(false);
        playerActive(false);
        safeExitFullscreen();
        isPlayerFullscreen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown, true);

    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [autoSelected, mediaPlayerStore, safeExitFullscreen, showDetails]);

  useEffect(() => {
    const onFullscreenChange = () => {
      if (document.fullscreenElement) {
        return;
      }
      // When switching from WOS to OSW, we intentionally exit native fullscreen.
      // Do not treat that transition as a signal to close the song.
      if (mediaPlayerStore.showOSW) {
        return;
      }
      if (showDetails || autoSelected) {
        setShowDetails(false);
        setAutoSelected(false);
      }
      mediaPlayerStore.setShowOSW(false);
      playerActive(false);
      isPlayerFullscreen(false);
    };

    document.addEventListener('fullscreenchange', onFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
    };
  }, [autoSelected, mediaPlayerStore, showDetails]);

  const [titleFontLoaded, titleFontError] = useFontLoaded(page.id, 'DuperBold');

  const parsedTitle =
    mediaPlayerStore.song.styleTitleSize >= 60
      ? splitByThreshold(title, 34)
      : [title];

  const titleFontSize = isMobile ? 18 : 34;
  const titleLineHeight = 1.15;
  const titleTopPadding = isMobile ? 3 : 6;
  const titleStageHeight = Math.ceil(
    parsedTitle.length * titleFontSize * titleLineHeight + titleTopPadding
  );

  const ActivitiesData = [
    {
      icon: '/img/by-yourself.svg',
      title: 'By Yourself',
      type: WonderlineActivityType.BY_YOURSELF,
    },
    {
      icon: '/img/in-pairs.svg',
      title: 'In Pairs',
      type: WonderlineActivityType.IN_PAIRS,
    },
    {
      icon: '/img/small-group.svg',
      title: 'Small Group',
      type: WonderlineActivityType.SMALL_GROUP,
    },
    {
      icon: '/img/large-group.svg',
      title: 'Large Group',
      type: WonderlineActivityType.LARGE_GROUP,
    },
    {
      icon: '/img/wonder-wall.svg',
      title: 'Wonder Wall',
      type: WonderlineActivityType.WONDER_WALL,
    },
    {
      icon: '/img/take-home.svg',
      title: 'Take Home',
      type: WonderlineActivityType.TAKE_HOME,
    },
  ];

  return (
    <div className="w-screen h-screen overflow-x-hidden overflow-y-scroll bg-white">
      <div className="mx-auto one-song-wonder">
        <div className="one-song-wonder__hero">
          <div className="inner-cont">
            <div className="flex items-center gap-4 lg:items-start lg:flex-col">
              <button
                type="button"
                className="one-song-wonder__mobile-arrow-icon"
                onClick={() => {
                  if (showDetails) {
                    setShowDetails(false);
                  }
                  if (!showDetails) {
                    mediaPlayerStore.setShowOSW(false);
                    playerActive(false);
                    safeExitFullscreen();
                    isPlayerFullscreen(false);
                  }
                  if (autoSelected) {
                    setAutoSelected(false);
                    setShowDetails(false);
                    playerActive(false);
                    mediaPlayerStore.setShowOSW(false);
                    safeExitFullscreen();
                    isPlayerFullscreen(false);
                  }
                  clearOswParamsFromUrl();
                }}
              >
                <img src="/img/arrow.svg" alt="arrow" />
              </button>
              {!shouldHideContent && (
                <Stage
                  width={isMobile ? 154 : 500}
                  height={titleStageHeight + 10}
                >
                  <Layer>
                    {(titleFontLoaded || titleFontError) &&
                      parsedTitle.map((line, idx) => (
                        <Text
                          key={line}
                          wrap="word"
                          text={line}
                          y={
                            titleTopPadding +
                            idx * (titleFontSize * titleLineHeight)
                          }
                          fill="#ffffff"
                          fontSize={titleFontSize}
                          lineHeight={titleLineHeight}
                          fontFamily="DuperBold"
                        />
                      ))}
                  </Layer>
                </Stage>
              )}
              <button
                type="button"
                className="back-cont"
                onClick={() => {
                  if (showDetails) {
                    setShowDetails(false);
                  }
                  if (!showDetails) {
                    mediaPlayerStore.setShowOSW(false);
                    playerActive(false);
                    safeExitFullscreen();
                    isPlayerFullscreen(false);
                  }
                  if (autoSelected) {
                    setAutoSelected(false);
                    setShowDetails(false);
                    playerActive(false);
                    mediaPlayerStore.setShowOSW(false);
                    safeExitFullscreen();
                    isPlayerFullscreen(false);
                  }
                  clearOswParamsFromUrl();
                }}
              >
                <div className="arrow-icon">
                  <img src="/img/arrow.svg" alt="arrow" />
                </div>
                {(!showDetails || autoSelected) && (
                  <p className="m-0">Back to the song</p>
                )}
                {showDetails && !autoSelected && (
                  <p className="m-0">Choose another line</p>
                )}
              </button>
            </div>
            <div className="items-end hidden lg:flex lg:flex-row gap-14">
              <button
                type="button"
                className="about-cont"
                onClick={() => showModal('wonder-settings-modal', false, 0)}
              >
                <img src="/img/question.svg" alt="arrow" />
                <p className="mb-0">What is a One-Song Wonder?</p>
              </button>
              <img
                src="/img/wonder-hero-icon.svg"
                alt="notifications"
                className="hidden osw-logo xl:block"
              />
            </div>
            <img
              src="/img/wonder-hero-icon.svg"
              alt="notifications"
              className="osw-logo-mobile"
            />
          </div>
        </div>
        <div className="one-song-wonder__sub-hero">
          <div>
            <h2 className="heading">
              Let’s <span style={{ color: '#7687c2' }}>wonder</span> about this
              song
            </h2>
            <h4 className="sub-head">
              Pick a line from the song to get started
            </h4>
          </div>
          <div
            className={`${
              showDetails && !shouldHideContent ? 'flex' : 'hidden'
            } items-center gap-8`}
          >
            <div dangerouslySetInnerHTML={{ __html: selectedLine }} />
            <a
              href=""
              data-dismiss="modal"
              id="share"
              onClick={(e) => {
                e.preventDefault();
                showModal('wonder-information-modal', false, 0);
              }}
            >
              <img
                src="/img/info-icon.svg"
                alt="Info icon"
                className="w-8 h-8 cursor-pointer"
              />
            </a>
          </div>
        </div>
        <div className="one-song-wonder__body-cont">
          <div className="one-song-wonder__sidebar" />
          <div
            className="one-song-wonder__body"
            style={
              !showDetails
                ? { left: isMobile ? '0' : '301px', marginTop: 0 }
                : { left: isMobile ? '0' : '77px', marginTop: 0 }
            }
          >
            {shouldHideContent && (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#7687c2]" />
              </div>
            )}
            {!shouldHideContent && !showDetails && (
              <>
                <h2 className="heading">
                  Let's <span style={{ color: '#7687c2' }}>wonder</span> about
                  this song
                </h2>
                <h4 className="sub-head">
                  Pick a line from the song to get started
                </h4>
                <div className="songlines-wrap">
                  {currentSongWonderLines.length > 1 &&
                    currentSongWonderLines.map(
                      (wonderline: any, idx: number) => (
                        <button
                          type="button"
                          className="one-song-wonder__song-line"
                          key={wonderline.songLine}
                          onClick={() => {
                            setShowDetails(true);
                            setSelectedWonderLine(wonderline);
                            setSelectedLineSteps(wonderline.wonderLineSteps);
                            setSelectedLineIndex(idx);
                            setSelectedLine(wonderline.songLine);
                            setSelectedStep(0);
                            updateWonderInUrl(idx, 0);
                          }}
                        >
                          <div className="number">{idx + 1}</div>
                          <div
                            className="line"
                            dangerouslySetInnerHTML={{
                              __html: wonderline.songLine,
                            }}
                          />
                          {/* <p className="line">{wonderline.songLine}</p> */}
                          <img
                            src="/img/right-chevron.svg"
                            alt="Chevron right icon"
                          />
                        </button>
                      )
                    )}
                </div>
              </>
            )}
            {!shouldHideContent && showDetails && !isLoadingNewSong && (
              <div className="one-song-wonder__details">
                <div className="one-song-wonder__steps">
                  {selectedLineSteps.map((step, idx) => {
                    const wonderStep = WonderSteps.find(
                      (ws) => ws.type === step.type
                    );
                    if (!wonderStep) return null;
                    return (
                      <button
                        className={getStepClassName(
                          selectedStep === idx,
                          idx === 0,
                          idx === selectedLineSteps.length - 1
                        )}
                        onClick={() => {
                          setSelectedStep(idx);
                          if (selectedLineIndex >= 0) {
                            if (selectedLineIndex >= 0) {
                              updateWonderInUrl(selectedLineIndex, idx);
                            }
                          }
                        }}
                        key={step.type}
                      >
                        {React.cloneElement(wonderStep.icon, {
                          fill: selectedStep === idx ? '#7687c2' : '#ffffff',
                        })}
                        <p>{wonderStep.title}</p>
                      </button>
                    );
                  })}
                </div>
                <div className="w-full">
                  <div className="items-center hidden gap-8 xl:flex">
                    <div
                      className="song-title"
                      dangerouslySetInnerHTML={{ __html: selectedLine }}
                    />
                    {/* <h3 className="song-title">“{selectedLine}”</h3> */}
                    <a
                      href=""
                      data-dismiss="modal"
                      id="share"
                      onClick={(e) => {
                        e.preventDefault();
                        showModal('wonder-information-modal', false, 0);
                      }}
                    >
                      <img
                        src="/img/info-icon.svg"
                        alt="Info icon"
                        className="object-contain w-8 h-8 cursor-pointer"
                      />
                    </a>
                  </div>
                  <div className="justify-between content-cont">
                    <div className="content">
                      <div
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                          __html:
                            selectedLineSteps?.[selectedStep]?.headline || '',
                        }}
                        className="content-heading"
                      />
                      {selectedLineSteps[selectedStep]?.content?.map(
                        (content: any) => {
                          if (
                            content.__typename === 'WonderLineStepContentImage'
                          ) {
                            return (
                              <img
                                key={content.key}
                                src={content.image}
                                alt="Wonder"
                              />
                            );
                          }
                          if (
                            content.__typename === 'WonderLineStepContentCopy'
                          ) {
                            const copyHtml =
                              content.copy || content.headline || '';
                            if (!copyHtml) return null;

                            return (
                              <div
                                key={content.key}
                                className="content-copy"
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                  __html: copyHtml,
                                }}
                              />
                            );
                          }
                          if (
                            content.__typename ===
                            'WonderLineStepContentQAReveal'
                          ) {
                            return (
                              <div
                                key={content.key}
                                className="content-qa-reveal"
                              >
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: content.copy,
                                  }}
                                />
                                {content.qas.map((qa: any) => (
                                  <QAReveal key={qa.key} qa={qa} />
                                ))}
                              </div>
                            );
                          }
                          return null;
                        }
                      )}
                    </div>
                    <div
                      className="flex flex-col justify-between md:gap-40 md:flex-row"
                      style={isMobile ? { width: '100%' } : { width: '263px' }}
                    >
                      <div className="w-full">
                        <div className="flex flex-col justify-between w-full md:gap-80 md:flex-row xl:block">
                          <button
                            type="button"
                            className="one-song-wonder__mobile-step-selector lg:hidden"
                            onClick={() =>
                              showModal('wonder-list-modal', false, 0)
                            }
                          >
                            <p>
                              {selectedLineSteps &&
                                selectedLineSteps[
                                  selectedStep
                                ]?.type.replaceAll('_', ' ')}
                            </p>
                            <img
                              src="/img/chevron-down-purple.svg"
                              alt="CHevron down"
                            />
                          </button>
                          {selectedWonderLine.wonderLineActivities.length >
                            0 && (
                            <button
                              type="button"
                              className="one-song-wonder__activities-trigger"
                              onClick={() => {
                                if (!isMobile) {
                                  setActivitiesOpen(!activitiesOpen);
                                }
                                if (isMobile) {
                                  showModal(
                                    'wonder-activities-modal',
                                    false,
                                    0
                                  );
                                }
                              }}
                            >
                              <p>Activities...</p>
                              <img
                                src="/img/chevron-down-purple.svg"
                                alt="CHevron down"
                              />
                            </button>
                          )}
                        </div>
                        {activitiesOpen && (
                          <div className="one-song-wonder__activities-dropdown">
                            {ActivitiesData.map((activity) => {
                              const actStep =
                                selectedWonderLine?.wonderLineActivities?.find(
                                  (ws: { type: string }) =>
                                    ws.type === activity.type
                                );
                              if (!actStep) return null;
                              return (
                                <button
                                  type="button"
                                  key={activity.title}
                                  className="activity"
                                  onClick={() => {
                                    if (selectedWonderLine) {
                                      const activities =
                                        selectedWonderLine.wonderLineActivities;
                                      const foundActivity = activities.find(
                                        (act: { type: string }) =>
                                          act.type === activity.type
                                      );
                                      if (foundActivity) {
                                        setSelectedActivity(foundActivity);
                                        showModal(
                                          'wonder-activity-modal',
                                          false,
                                          0
                                        );
                                      }
                                    }
                                  }}
                                >
                                  <img
                                    src={activity.icon}
                                    alt={activity.title}
                                  />{' '}
                                  <p>{activity.title}</p>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <WonderInformation
        selectedWonderLine={selectedWonderLine}
        uniqueWonderline={currentSongWonderLines.length > 1}
      />
      <ActivitiesModal selectedActivity={selectedActivity} />
      <WonderListModal setSelectedStep={setSelectedStep} />
      <WonderActivitiesModal
        selectedWonderLine={selectedWonderLine}
        setSelectedActivity={setSelectedActivity}
      />
      {!settingLoading && (
        <WonderSettings settings={settingData?.getSetting?.value} />
      )}
    </div>
  );
};

export default OneSongWonder;
